import Head from 'next/head';
import React, {useContext, useMemo} from 'react';
import MetaTitle from './MetaTitle';
import {REGION, SNS_IMAGE, LOCALE, WEB_HOST} from '../constant';
import {Page} from '../constant/page';
import {PageAppContext} from '../context';
import useTheme from '../hook/useTheme';
import useTypedSelector from '../hook/useTypedSelector';
import {IAuthorDetails, IRegion} from '../type';
import {getUrl} from '../util/PageRouter';

/**
 * 참고 사이트
 * 오픈 그래프 문서: https://ogp.me/
 * 페이스북 공유 디버깅: https://developers.facebook.com/tools/debug/sharing/
 * 트위터 공유 디버깅: https://cards-dev.twitter.com/validator
 * 기타 head 태그 정보: https://github.com/joshbuchea/HEAD#icons
 */
export default function Meta() {
  const {page} = useContext(PageAppContext);
  const {title, currentUrl} = useContext(PageAppContext);
  const [content, episode, viewerContent, eventId] = useTypedSelector(state => [
    state.content.currentId
      ? state.content.contentMap[state.content.currentId]
      : undefined,
    state.viewer.episode,
    state.viewer.content,
    state.event.moduleMap.event?.commonModule.eventId,
  ]);
  const {isLightMode} = useTheme();
  const hasMetaTitle = useMemo(
    () => SELF_META_TITLE_PAGES.includes(page),
    [page]
  );

  function getCanonicalUrl() {
    switch (page) {
      case Page.Content:
        return content
          ? WEB_HOST +
              getUrl({page: Page.Content, id: content.id, sid: content.sid})
          : '';
      case Page.Home:
        return WEB_HOST;
      case Page.Viewer:
        return episode
          ? WEB_HOST +
              getUrl({page: Page.Viewer, id: episode.id, sid: episode.sid})
          : '';
      case Page.Event:
        return eventId ? WEB_HOST + getUrl({page: Page.Event, eventId}) : '';
      default:
        return '';
    }
  }
  const canonicalUrl = getCanonicalUrl();

  function getMetaDescription() {
    switch (page) {
      case Page.Content:
        return content?.synopsis;
      case Page.Viewer:
        return viewerContent?.synopsis;
      default:
        return DESCRIPTION_MAP[REGION];
    }
  }
  const description = getMetaDescription();

  function getMetaImage() {
    switch (page) {
      case Page.Content:
        return content?.shareImg;
      case Page.Viewer:
        return episode?.thumbnail;
      default:
        return SNS_IMAGE;
    }
  }
  const image = getMetaImage();

  function getMetaKeywords() {
    switch (page) {
      case Page.Content:
        return `${content?.title}, ${getAuthorNames(content?.authorDetails)}`;
      case Page.Viewer:
        return `${viewerContent?.title}, ${getAuthorNames(
          viewerContent?.authorDetails
        )}, ${episode?.title}`;
      default:
        return KEYWORDS_MAP[REGION];
    }
  }
  const keywords = getMetaKeywords();

  return (
    <>
      {/**
       * `title`이 변해야되거나 없는 페이지들은 `MetaTitle`을 직접 사용하고 있다.
       * `MetaTitle`이 없는 페이지들만 `MetaTitle`을 넣어준다.
       */}
      {!hasMetaTitle && <MetaTitle page={page} title={title} />}
      <Head>
        {/* iOS 15 이상 Safari의 Layoutd이 Tab Bar일 경우 브라우저에게 해당 컬러를 알려줘야한다.
            https://useyourloaf.com/blog/safari-15-theme-color/ */}
        <meta
          name="theme-color"
          content={isLightMode ? '#ffffff' : '#121212'}
        />
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta property="og:site_name" content={SITE_NAME[REGION]} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={image} />
        <meta property="og:locale" content={LOCALE[REGION]} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={currentUrl} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={SITE_ACCOUNT[REGION]} />
        <meta name="twitter:url" content={currentUrl} />
        {!SEO_PAGES.includes(page) ? (
          <meta name="robots" content="noindex,nofollow" />
        ) : (
          Page.Viewer === page && <meta name="robots" content="noimageindex" />
        )}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      </Head>
    </>
  );
}

const SEO_PAGES = [Page.Home, Page.Content, Page.Viewer, Page.Event];
const SELF_META_TITLE_PAGES = [
  Page.Event,
  Page.Content,
  Page.More,
  Page.MorePolicyChildPersonalThirdParty,
  Page.MorePersonalInformationShort,
  Page.MyPageEpisodeList,
  Page.Viewer,
  Page.WebviewNotice,
];

export const DESCRIPTION_MAP: {[key in IRegion]: string} = {
  kor: '카카오웹툰에서 상상력을 자극하는 웹툰들을 만나보세요. 다양한 장르의 새로운 웹툰들을 즐기실 수 있습니다!',
  twn: '隨時隨地都能盡情閱讀漫畫的KAKAO WEBTOON！加入KAKAO WEBTOON，每天免費觀賞各式各樣的漫畫。',
  tha: 'สนุกกับ KAKAO WEBTOON ได้ทุกที่ทุกเวลา พบกับการ์ตูนสนุก หลากหลายแนว ได้ฟรีทุกวันที่ KAKAO WEBTOON',
  idn: 'Kapanpun dan dimanapun dapat nikmati cerita KAKAO WEBTOON. Nikmati berbagai genre cerita yang menarik yang gratis setiap harinya.',
};
const KEYWORDS_MAP: {[key in IRegion]: string} = {
  kor: '웹툰, 기다무, 기다리면무료, 무료만화',
  twn: '網漫, 網路漫畫, 漫畫, 韓漫, 韓國網漫, 韓國漫畫, 免費漫畫, 線上漫畫, 漫畫線上看, 看漫畫',
  tha: 'Webtoon, Online comic, Korean comic, Korean Webtoon, free',
  idn: 'Webtoon, Online Comic, Komik korea, Manhwa, Webcomics, Free Comics',
};

function getAuthorNames(authorDetails?: IAuthorDetails) {
  if (!authorDetails) return '';
  return authorDetails
    .reduce<string[]>((acc, item) => {
      item.names.forEach(name => !acc.includes(name) && acc.push(name));
      return acc;
    }, [])
    .join(', ');
}

export const SITE_NAME: {[key in IRegion]: string} = {
  kor: '카카오웹툰',
  twn: 'KAKAO WEBTOON',
  tha: 'KAKAO WEBTOON',
  idn: 'KAKAO WEBTOON',
};

const SITE_ACCOUNT: {[key in IRegion]: string} = {
  kor: '@kakaowebtoon_kr',
  twn: '',
  tha: '@kakaowebtoon_th',
  idn: '@kakaowebtoon_id',
};
